// WebGPU initialization — generic, no game knowledge

export const DEPTH_FORMAT: GPUTextureFormat = "depth24plus";

export type GPU = {
  device: GPUDevice;
  context: GPUCanvasContext;
  format: GPUTextureFormat;
  canvas: HTMLCanvasElement;
  depthTexture: GPUTexture;
  hasTimestamps: boolean;
  querySet: GPUQuerySet | null;
  queryBuffer: GPUBuffer | null;
  readBuffer: GPUBuffer | null;
};

export async function initGPU(canvas: HTMLCanvasElement): Promise<GPU> {
  if (!navigator.gpu) {
    throw new Error("WebGPU not supported in this browser");
  }

  const adapter = await navigator.gpu.requestAdapter();
  if (!adapter) {
    throw new Error("No WebGPU adapter found");
  }

  const hasTimestamps = adapter.features.has("timestamp-query");
  const device = await adapter.requestDevice({
    requiredFeatures: hasTimestamps ? ["timestamp-query"] : [],
  });
  const context = canvas.getContext("webgpu")!;
  const format = navigator.gpu.getPreferredCanvasFormat();

  context.configure({ device, format, alphaMode: "opaque" });

  const depthTexture = device.createTexture({
    size: [canvas.width, canvas.height],
    format: DEPTH_FORMAT,
    usage: GPUTextureUsage.RENDER_ATTACHMENT | GPUTextureUsage.TEXTURE_BINDING | GPUTextureUsage.COPY_SRC,
  });

  // GPU timestamp queries (if supported)
  let querySet: GPUQuerySet | null = null;
  let queryBuffer: GPUBuffer | null = null;
  let readBuffer: GPUBuffer | null = null;

  if (hasTimestamps) {
    querySet = device.createQuerySet({ type: "timestamp", count: 2 });
    queryBuffer = device.createBuffer({ size: 16, usage: GPUBufferUsage.QUERY_RESOLVE | GPUBufferUsage.COPY_SRC });
    readBuffer = device.createBuffer({ size: 16, usage: GPUBufferUsage.MAP_READ | GPUBufferUsage.COPY_DST });
    console.log("WebGPU initialized (with GPU timestamps)");
  } else {
    console.log("WebGPU initialized (no timestamp support)");
  }

  return { device, context, format, canvas, depthTexture, hasTimestamps, querySet, queryBuffer, readBuffer };
}

// Create a render target texture
export function createRenderTarget(
  device: GPUDevice,
  width: number,
  height: number,
  format: GPUTextureFormat,
  extraUsage: GPUTextureUsageFlags = 0,
): GPUTexture {
  return device.createTexture({
    size: [width, height],
    format,
    usage: GPUTextureUsage.RENDER_ATTACHMENT | GPUTextureUsage.TEXTURE_BINDING | extraUsage,
  });
}
